import type { PreviewSupport, Strategy } from '@/app/lib/quant/types'

export interface InstrumentOffer {
  instrument: string | null
  offered: string[]
  mismatch: { previewInstrument: string; formInstrument: string } | null
  disabledReason: string | null
}

/** Which instrument the preview shows, given what it offers and what the form allows (spec 7.2/7.3). */
export function resolveInstrumentOffer(
  preview: PreviewSupport,
  formInstrument: string | null,
  formEnum: string[] | null,
): InstrumentOffer {
  const offered = preview.instruments.filter((i) => formEnum === null || formEnum.includes(i))
  if (offered.length === 0) {
    return {
      instrument: null,
      offered,
      mismatch: null,
      disabledReason: 'No instrument is offered by both the preview and the form.',
    }
  }
  if (formInstrument !== null && offered.includes(formInstrument)) {
    return { instrument: formInstrument, offered, mismatch: null, disabledReason: null }
  }
  const first = offered[0]
  const mismatch =
    formInstrument !== null && formInstrument !== '' ? { previewInstrument: first, formInstrument } : null
  return { instrument: first, offered, mismatch, disabledReason: null }
}

export function mismatchLine(m: { previewInstrument: string; formInstrument: string }): string {
  return `Preview shows ${m.previewInstrument}; the form says ${m.formInstrument}. Pick ${m.previewInstrument} to run the backtest on it.`
}

/** Why a strategy has no live preview, or `null` when it has one (spec 9.7). */
export function noPreviewReason(strategy: Strategy): string | null {
  if (typeof strategy.preview_error === 'string' && strategy.preview_error !== '') {
    return `The live preview is broken for this strategy: ${strategy.preview_error}`
  }
  if (strategy.preview === null || strategy.preview === undefined) {
    return `No live preview for ${strategy.name ?? strategy.slug}. The parameters and Start work as usual.`
  }
  return null
}

/** `Missing days: a, b ... and N more.` (first 20 only); `null` when there are none. */
export function formatMissingDays(days: string[] | undefined, count: number | undefined): string | null {
  if (days === undefined || days.length === 0) return null
  const shown = days.slice(0, 20)
  const list = shown.join(', ')
  if (count !== undefined && count > shown.length) return `Missing days: ${list} ... and ${count - shown.length} more.`
  return `Missing days: ${list}.`
}

/** `YYYY-MM-DD` in UTC. */
export function utcDate(epochSeconds: number): string {
  return new Date(epochSeconds * 1000).toISOString().slice(0, 10)
}
